import { calculateVatDocument, Decimal, Money } from "@station/core";
import { applyCampaigns } from "./apply.js";
import { CREDIT_TENDER_TYPES, defaultUnitPriceScale, lineGross, } from "./types.js";
const MAX = (a, b) => (a.gt(b) ? a : b);
/** Sepeti hesaplar: kampanya → indirim → KDV kırılımı → toplam → ödeme → veresiye kararı. */
export function computeBasket(input) {
    const issues = [];
    const lines = input.lines;
    if (lines.length === 0)
        issues.push({ code: "EMPTY_BASKET", message: "Sepet boş." });
    const seen = new Set();
    for (const line of lines) {
        if (seen.has(line.lineNo)) {
            issues.push({
                code: "DUPLICATE_LINE_NO",
                message: `Satır numarası yinelendi: ${line.lineNo}`,
                meta: { lineNo: String(line.lineNo) },
            });
        }
        seen.add(line.lineNo);
        if (Decimal.from(line.quantity).isNegative()) {
            issues.push({
                code: "NEGATIVE_QUANTITY",
                message: `Satır ${line.lineNo}: miktar negatif olamaz.`,
                meta: { lineNo: String(line.lineNo) },
            });
        }
        if (line.kind === "FUEL" && !line.fuelAgentTxId) {
            issues.push({
                code: "FUEL_LINE_WITHOUT_TX",
                message: `Satır ${line.lineNo}: akaryakıt satırı bekleyen bir dolum işlemine bağlanmalıdır.`,
                meta: { lineNo: String(line.lineNo) },
            });
        }
    }
    const grossByLine = new Map();
    for (const line of lines) {
        const scale = line.unitPriceScale ?? defaultUnitPriceScale(line.kind);
        grossByLine.set(line.lineNo, lineGross(line.unitPrice, line.quantity, scale));
    }
    const campaignDiscounts = input.campaigns?.length
        ? applyCampaigns(lines, input.campaigns, input.context)
        : [];
    const discounts = [...campaignDiscounts, ...(input.manualDiscounts ?? [])];
    const discountByLine = new Map();
    for (const discount of discounts) {
        const gross = grossByLine.get(discount.lineNo);
        if (!gross) {
            issues.push({
                code: "UNKNOWN_DISCOUNT_LINE",
                message: `İndirim bilinmeyen satıra bağlı: ${discount.lineNo}`,
                meta: { lineNo: String(discount.lineNo) },
            });
            continue;
        }
        const amount = Money.fromMinor(discount.amount);
        discountByLine.set(discount.lineNo, (discountByLine.get(discount.lineNo) ?? Money.ZERO).add(amount));
    }
    for (const [lineNo, discount] of discountByLine) {
        const gross = grossByLine.get(lineNo);
        if (discount.gt(gross) || discount.isNegative()) {
            issues.push({
                code: "DISCOUNT_EXCEEDS_GROSS",
                message: `Satır ${lineNo}: indirim satır tutarını aşamaz.`,
                meta: { lineNo: String(lineNo), gross: gross.toString(), discount: discount.toString() },
            });
        }
    }
    // KDV: fiyat KDV DÂHİL; oran bazında toplamdan hesaplanır, satırlara kuruş kaybı olmadan dağıtılır.
    const netByLine = lines.map((line) => {
        const gross = grossByLine.get(line.lineNo);
        const discount = discountByLine.get(line.lineNo) ?? Money.ZERO;
        const net = gross.sub(discount);
        return net.isNegative() ? Money.ZERO : net;
    });
    const vatDocument = calculateVatDocument(lines.map((line, index) => ({ amount: netByLine[index], ratePercent: line.vatRatePercent })), "VAT_INCLUSIVE");
    const resultLines = lines.map((line, index) => {
        const gross = grossByLine.get(line.lineNo);
        const discount = discountByLine.get(line.lineNo) ?? Money.ZERO;
        const breakdown = vatDocument.lines[index];
        return {
            lineNo: line.lineNo,
            kind: line.kind,
            productId: line.productId,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            gross: gross.toString(),
            discount: discount.toString(),
            net: netByLine[index].toString(),
            vatBase: breakdown.net.toString(),
            vat: breakdown.vat.toString(),
            vatRateCode: line.vatRateCode,
            vatRatePercent: line.vatRatePercent,
        };
    });
    const totalGross = Money.sum([...grossByLine.values()]);
    const totalDiscount = Money.sum([...discountByLine.values()]);
    const due = vatDocument.totals.gross;
    const totals = {
        gross: totalGross.toString(),
        discount: totalDiscount.toString(),
        net: due.toString(),
        vatBase: vatDocument.totals.net.toString(),
        vatTotal: vatDocument.totals.vat.toString(),
        vat: vatDocument.byRate.map((row) => ({
            rate: Decimal.from(row.ratePercent).toString(),
            base: row.net.toString(),
            vat: row.vat.toString(),
            gross: row.gross.toString(),
        })),
    };
    // ── Ödeme ───────────────────────────────────────────────────────────────────
    const tenders = input.tenders ?? [];
    const cash = Money.sum(tenders.filter((tender) => tender.type === "CASH").map((t) => Money.fromMinor(t.amount)));
    const nonCash = Money.sum(tenders.filter((tender) => tender.type !== "CASH").map((t) => Money.fromMinor(t.amount)));
    // Para üstü YALNIZCA nakitten verilir: nakit dışı tahsilat önce borca sayılır.
    const nonCashApplied = nonCash.gt(due) ? due : nonCash;
    const cashDue = MAX(due.sub(nonCashApplied), Money.ZERO);
    const changeGiven = MAX(cash.sub(cashDue), Money.ZERO);
    const remaining = MAX(due.sub(nonCashApplied).sub(cash), Money.ZERO);
    const overpaid = MAX(nonCash.sub(due), Money.ZERO);
    if (overpaid.isPositive()) {
        issues.push({
            code: "PAYMENT_OVERPAID",
            message: "Nakit dışı tahsilat toplamı tutarı aşıyor; para üstü yalnızca nakitten verilir.",
            meta: { overpaid: overpaid.toString() },
        });
    }
    if (tenders.length > 0 && remaining.isPositive()) {
        issues.push({
            code: "PAYMENT_INCOMPLETE",
            message: "Tahsilat eksik.",
            meta: { remaining: remaining.toString() },
        });
    }
    // Toplam para üstü SON nakit satırına yazılır (ajan `Σ tenders − Σ changeGiven` kimliğini denetler).
    let lastCashIndex = -1;
    tenders.forEach((tender, index) => {
        if (tender.type === "CASH")
            lastCashIndex = index;
    });
    const resultTenders = tenders.map((tender, index) => ({
        ...tender,
        changeGiven: index === lastCashIndex ? changeGiven.toString() : "0",
    }));
    const payment = {
        due: due.toString(),
        cashTendered: cash.toString(),
        nonCashTendered: nonCash.toString(),
        tendered: cash.add(nonCash).toString(),
        changeGiven: changeGiven.toString(),
        remaining: remaining.toString(),
        overpaid: overpaid.toString(),
        settled: remaining.isZero() && overpaid.isZero() && tenders.length > 0,
    };
    // ── Veresiye / filo ─────────────────────────────────────────────────────────
    const creditAmount = Money.sum(tenders
        .filter((tender) => CREDIT_TENDER_TYPES.includes(tender.type))
        .map((tender) => Money.fromMinor(tender.amount)));
    let credit = null;
    if (creditAmount.isPositive() && input.credit) {
        const tolerance = Money.fromMinor(input.credit.toleranceKurus ?? "0");
        const available = input.credit.availableCredit === null ? null : Money.fromMinor(input.credit.availableCredit);
        let verdict;
        if (input.credit.blocked)
            verdict = "BLOCKED";
        else if (available === null || available.gte(creditAmount))
            verdict = "ALLOWED";
        else if (available.add(tolerance).gte(creditAmount))
            verdict = "TOLERANCE";
        else
            verdict = "LIMIT_EXCEEDED";
        credit = {
            counterpartyId: input.credit.counterpartyId,
            verdict,
            amount: creditAmount.toString(),
            availableBefore: available === null ? null : available.toString(),
            availableAfter: available === null ? null : available.sub(creditAmount).toString(),
            tolerance: tolerance.toString(),
            requiresSupervisor: verdict === "LIMIT_EXCEEDED" || verdict === "BLOCKED",
            source: "CACHE",
        };
        if (verdict === "BLOCKED") {
            issues.push({ code: "CREDIT_BLOCKED", message: "Cari hesap veresiye satışa kapalı." });
        }
        else if (verdict === "LIMIT_EXCEEDED") {
            issues.push({
                code: "CREDIT_LIMIT",
                message: "Cari limiti yetersiz; müdür onayı gerekir.",
                meta: { available: available === null ? "" : available.toString(), amount: creditAmount.toString() },
            });
        }
    }
    const requiresAgeConfirmation = lines.some((line) => line.ageRestricted === true);
    const blockingCodes = [
        "EMPTY_BASKET",
        "DUPLICATE_LINE_NO",
        "NEGATIVE_QUANTITY",
        "DISCOUNT_EXCEEDS_GROSS",
        "UNKNOWN_DISCOUNT_LINE",
        "FUEL_LINE_WITHOUT_TX",
        "PAYMENT_OVERPAID",
        "TOTALS_MISMATCH",
    ];
    return {
        lines: resultLines,
        discounts,
        tenders: resultTenders,
        totals,
        payment,
        credit,
        issues,
        requiresAgeConfirmation,
        sellable: !issues.some((issue) => blockingCodes.includes(issue.code)),
    };
}
//# sourceMappingURL=basket.js.map